import { useModal } from "@/Contexts";
import { useGlobalAlert } from "@/States/globalAlert";
import { useNavigate } from "@/Utils/useNavigate";
import useCustomers from "@/api/hooks/useCustomers";
import { useSlackNotifications } from "@/api/hooks/useSlackNotifications";
import ErrorNotice from "@/components/ErrorNotice";
import ModalContainer from "@/components/modalContainer";

import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { isEmpty } from "lodash";

import { MWMCustomerFormValues } from "../../types";
import { CreateCustomerForm } from "./CustomerForm";

export const CreateCustomerModal = () => {
  const navigate = useNavigate();
  const { hideModal } = useModal();
  const { sendNewCustomerSlackNotification } = useSlackNotifications();
  const { setGlobalAlert } = useGlobalAlert();
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const {
    refetchCustomers,
    createCustomer,
    isSavingCustomer,
    isLoadingCustomers,
    isError,
  } = useCustomers();
  const containerRef = useRef<HTMLDivElement>(null);

  const formHandler = useForm<MWMCustomerFormValues>({
    defaultValues: { accessParents: [{}] },
  });

  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = formHandler;

  const setupCustomer = useCallback(
    (formData: MWMCustomerFormValues) => {
      const { accessParents, ...customer } = formData;
      const accessParentIds =
        accessParents?.filter((el) => el.id).map((el) => ({ id: el.id })) ?? []; // Filter out empty accessParents

      createCustomer(
        { customer: { ...customer, accessParents: accessParentIds } },
        {
          onSuccess: async (createdCustomer) => {
            await refetchCustomers();
            hideModal();
            setGlobalAlert({
              type: "success",
              message: "systemMessages:customerAdded",
            });
            sendNewCustomerSlackNotification(
              createdCustomer.name,
              createdCustomer.id,
            );
            navigate(`/customer/manage/${createdCustomer.id}`);
          },
          onError: () => {
            setGlobalAlert({
              type: "error",
              message: "errors:failedSave",
            });
          },
        },
      );
    },
    [
      createCustomer,
      hideModal,
      refetchCustomers,
      setGlobalAlert,
      sendNewCustomerSlackNotification,
      navigate,
    ],
  );

  const onSubmit = handleSubmit((customerWithAccessParents) => {
    setupCustomer(customerWithAccessParents);
  });

  return (
    <ModalContainer
      title="actions:createCustomer"
      onConfirm={() => {
        setIsSubmitAttempted(true);
        onSubmit();
      }}
      onCancel={hideModal}
      onConfirmLoading={isSavingCustomer || isLoadingCustomers}
      onConfirmDisabled={
        isError || (isSubmitAttempted && (!isEmpty(errors) || !isValid))
      }
      className="w-11/12 lg:w-3/5"
      contentContainerRef={containerRef}
      showDiscardModal={isDirty}
    >
      {isError ? (
        <ErrorNotice />
      ) : (
        <div className="flex h-full flex-col gap-6">
          <CreateCustomerForm formHandler={formHandler} />
        </div>
      )}
    </ModalContainer>
  );
};
